import { useMemo, useState } from "react";
import Tag, { type TagType } from "@/components/Tag/Tag";
import { SkeletonList } from "@/components/Skeleton";
import { toDateFormat } from "@/lib/dateHelpers";
import type { TimelineActivityDay, TimelineActivityYear, TimelineCategory } from "../utils";

const dayLabels = ["", "Mon", "", "Wed", "", "Fri", ""];

const intensityClasses: Record<TimelineActivityDay["intensity"], string> = {
  0: "bg-sys-brand-surface-container-highest border-sys-brand-outline-variant",
  1: "bg-sys-brand-primary-container-bright border-sys-brand-primary-container-bright",
  2: "bg-sys-brand-primary-container border-sys-brand-primary-container",
  3: "bg-sys-brand-primary border-sys-brand-primary",
  4: "bg-extended-success-brand-color border-extended-success-brand-color"
};

const tagTypes: Record<TimelineCategory, TagType> = {
  Membership: "primary",
  Section: "tertiary",
  Education: "information",
  Career: "success",
  Prospect: "warn"
};

function getDayTitle(day: TimelineActivityDay) {
  const formattedDate = toDateFormat(day.date) || day.date;

  if (!day.events.length) {
    return `No timeline events on ${formattedDate}`;
  }

  return `${day.events.length} timeline ${day.events.length === 1 ? "event" : "events"} on ${formattedDate}: ${day.events
    .map((event) => event.title)
    .join(", ")}`;
}

function flattenDays(years: TimelineActivityYear[]) {
  return years.flatMap((year) => year.weeks.flatMap((week) => week)).filter((day) => day.isCurrentYear);
}

function getYearEventCount(year: TimelineActivityYear) {
  return flattenDays([year]).reduce((total, day) => total + day.events.length, 0);
}

export default function TimelineActivityHeatmap({
  years,
  loading
}: {
  years: TimelineActivityYear[];
  loading?: boolean;
}) {
  const currentCalendarYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const selectedActivityYear =
    years.find((year) => year.year === selectedYear) ||
    years.find((year) => year.year === currentCalendarYear) ||
    years[0];
  const activeDays = useMemo(
    () =>
      flattenDays(selectedActivityYear ? [selectedActivityYear] : [])
        .filter((day) => day.events.length > 0)
        .sort((a, b) => b.date.localeCompare(a.date)),
    [selectedActivityYear]
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const selectedDay = activeDays.find((day) => day.date === selectedDate) || activeDays[0];

  if (loading) {
    return (
      <div className="mb-10" data-testid="activity-heatmap-skeleton">
        <SkeletonList rows={4} />
      </div>
    );
  }

  if (!years.length) {
    return null;
  }

  return (
    <section
      className="mb-10 rounded-3xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest p-6"
      aria-labelledby="timeline-activity-heatmap-heading"
      data-testid="timeline-activity-heatmap"
    >
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 id="timeline-activity-heatmap-heading" className="text-title-large-bold text-sys-brand-on-surface">
            Activity overview
          </h2>
          <p className="mt-1 text-body-medium text-sys-brand-on-surface-variant">
            Each square represents a day with timeline milestones. Select an active day to review its events.
          </p>
        </div>
        <div className="flex items-center gap-2 text-label-small text-sys-brand-on-surface-variant">
          <span>Less</span>
          {[0, 1, 2, 3, 4].map((intensity) => (
            <span
              key={intensity}
              className={`h-3 w-3 rounded-sm border ${intensityClasses[intensity as TimelineActivityDay["intensity"]]}`}
            />
          ))}
          <span>More</span>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <aside className="lg:w-36 lg:shrink-0" aria-label="Timeline activity years">
          <div className="mb-2 text-label-medium text-sys-brand-on-surface-variant">Years</div>
          <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-x-visible lg:pb-0">
            {years.map((year) => {
              const isSelected = selectedActivityYear?.year === year.year;
              const eventCount = getYearEventCount(year);

              return (
                <button
                  key={year.year}
                  type="button"
                  aria-label={`View ${year.year} timeline activity`}
                  aria-pressed={isSelected}
                  onClick={() => {
                    setSelectedYear(year.year);
                    setSelectedDate(null);
                  }}
                  className={`rounded-xl border px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-sys-brand-primary ${
                    isSelected
                      ? "border-sys-brand-primary bg-sys-brand-primary-container-bright text-sys-brand-on-primary-container"
                      : "border-sys-brand-outline-variant bg-sys-brand-surface text-sys-brand-on-surface hover:bg-sys-brand-surface-container"
                  }`}
                >
                  <div className="text-label-large">{year.year}</div>
                  <div className="text-label-small opacity-80">
                    {eventCount} {eventCount === 1 ? "event" : "events"}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {selectedActivityYear && (
          <div className="min-w-0 flex-1">
            <div className="overflow-x-auto pb-2">
              <div className="min-w-max">
                <h3 className="mb-3 text-title-medium text-sys-brand-on-surface">{selectedActivityYear.year}</h3>
                <div
                  className="grid gap-[3px]"
                  style={{
                    gridTemplateColumns: `32px repeat(${selectedActivityYear.weeks.length}, 12px)`,
                    gridTemplateRows: "20px repeat(7, 12px)"
                  }}
                >
                  {selectedActivityYear.monthLabels.map((month) => (
                    <div
                      key={`${selectedActivityYear.year}-${month.label}`}
                      className="text-label-small text-sys-brand-on-surface-variant"
                      style={{ gridColumn: `${month.weekIndex + 2} / span 4`, gridRow: 1 }}
                    >
                      {month.label}
                    </div>
                  ))}
                  {dayLabels.map((label, index) => (
                    <div
                      key={`${selectedActivityYear.year}-day-label-${index}`}
                      className="text-label-small leading-3 text-sys-brand-on-surface-variant"
                      style={{ gridColumn: 1, gridRow: index + 2 }}
                    >
                      {label}
                    </div>
                  ))}
                  {selectedActivityYear.weeks.flatMap((week) =>
                    week.map((day) => (
                      <button
                        key={day.date}
                        type="button"
                        title={getDayTitle(day)}
                        aria-label={getDayTitle(day)}
                        disabled={!day.isCurrentYear || day.events.length === 0}
                        onClick={() => setSelectedDate(day.date)}
                        className={`h-3 w-3 rounded-sm border ${
                          day.isCurrentYear ? intensityClasses[day.intensity] : "border-transparent bg-transparent"
                        } ${
                          day.events.length > 0
                            ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-sys-brand-primary"
                            : "cursor-default"
                        } ${selectedDay?.date === day.date ? "ring-2 ring-sys-brand-primary" : ""}`}
                        style={{ gridColumn: day.weekIndex + 2, gridRow: day.dayIndex + 2 }}
                        data-testid={day.events.length > 0 ? "activity-day" : undefined}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>

            {selectedDay && (
              <div
                className="mt-6 rounded-2xl border border-sys-brand-outline-variant bg-sys-brand-surface p-4"
                data-testid="activity-day-overview"
              >
                <div className="text-title-medium text-sys-brand-on-surface">{toDateFormat(selectedDay.date)}</div>
                <div className="mt-1 text-body-medium text-sys-brand-on-surface-variant">
                  {selectedDay.events.length} timeline {selectedDay.events.length === 1 ? "event" : "events"}
                </div>
                <div className="mt-4 flex flex-col gap-3">
                  {selectedDay.events.map((event) => (
                    <div
                      key={event.id}
                      className="flex flex-col gap-2 rounded-xl bg-sys-brand-surface-container-low p-3 md:flex-row md:items-start md:justify-between"
                    >
                      <div>
                        <div className="text-body-medium text-sys-brand-on-surface">{event.title}</div>
                        {event.description && (
                          <div className="text-body-small text-sys-brand-on-surface-variant">{event.description}</div>
                        )}
                      </div>
                      <Tag tagType={tagTypes[event.category]} tagStyle="standard" size="small">
                        {event.category}
                      </Tag>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
